import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cubicOut } from 'svelte/easing';
import type { TransitionConfig } from 'svelte/transition';
import type { Activity } from '#api/activityApi.ts';

export type WithElementRef<T, E extends HTMLElement = HTMLElement> = T & {
    ref?: E | null;
};

export type { WithoutChild, WithoutChildren, WithoutChildrenOrChild } from 'bits-ui';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

type FlyAndScaleParams = {
    y?: number;
    x?: number;
    start?: number;
    duration?: number;
};

export const flyAndScale = (
    node: Element,
    params: FlyAndScaleParams = { y: -8, x: 0, start: 0.95, duration: 150 }
): TransitionConfig => {
    const style = getComputedStyle(node);
    const transform = style.transform === 'none' ? '' : style.transform;

    const scaleConversion = (valueA: number, scaleA: [number, number], scaleB: [number, number]) => {
        const [minA, maxA] = scaleA;
        const [minB, maxB] = scaleB;

        const percentage = (valueA - minA) / (maxA - minA);
        const valueB = percentage * (maxB - minB) + minB;

        return valueB;
    };

    const styleToString = (style: Record<string, number | string | undefined>): string => {
        return Object.keys(style).reduce((str, key) => {
            if (style[key] === undefined) return str;
            return str + `${key}:${style[key]};`;
        }, '');
    };

    return {
        duration: params.duration ?? 200,
        delay: 0,
        css: (t) => {
            const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0]);
            const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0]);
            const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1]);

            return styleToString({
                transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
                opacity: t
            });
        },
        easing: cubicOut
    };
};

export function generateNewName(name: string): string {
    // Check if name already contains a number appended
    const regex = /_(\d+)$/;
    const match = name.match(regex);

    if (match) {
        const number = parseInt(match[1]); // Extract the number from the matched string
        const newName = name.replace(regex, `_${number + 1}`); // Increment the number and append to the name
        return newName;
    } else {
        // If no number is present, append _1 to the name
        return `${name}_1`;
    }
}

export function formatAuditValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
}

export type AuditChangeRow = { property: string; old?: unknown; new?: unknown; hidden?: boolean };

export function getAuditChanges(activity: Activity): AuditChangeRow[] {
    return (activity.audit?.changes ?? []).map((change) =>
        change.isHidden
            ? { property: change.property, hidden: true, old: '[hidden]', new: '[hidden]' }
            : { property: change.property, old: change.oldValue ?? undefined, new: change.newValue ?? undefined }
    );
}

export type EntityChange = {
    entity: string;
    entityId: string;
    action: string;
    key?: Record<string, unknown>;
    old?: Record<string, unknown>;
    new?: Record<string, unknown>;
};

export type EntityChangeGroup = {
    entity: string;
    entityId: string;
    action: string;
    changes: { property: string; old?: string; new?: string }[];
};

// Undefined renders as the audit UI's "N/A" placeholder, like AuditActivityContent's formatInlineValue.
function entityChangeText(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

export function getEntityAuditChanges(activity: Activity): EntityChangeGroup[] {
    const entityChanges = activity.meta?.changes as EntityChange[] | undefined;

    if (!entityChanges || !Array.isArray(entityChanges)) {
        return [];
    }

    return entityChanges.map((entityChange) => {
        const changes: { property: string; old?: string; new?: string }[] = [];

        const allProperties = new Set<string>();
        if (entityChange.old) {
            Object.keys(entityChange.old).forEach((key) => allProperties.add(key));
        }
        if (entityChange.new) {
            Object.keys(entityChange.new).forEach((key) => allProperties.add(key));
        }

        for (const property of allProperties) {
            const oldValue = entityChange.old?.[property];
            const newValue = entityChange.new?.[property];

            changes.push({ property, old: entityChangeText(oldValue), new: entityChangeText(newValue) });
        }

        return {
            entity: entityChange.entity,
            entityId: entityChange.entityId,
            action: entityChange.action,
            changes
        };
    });
}
